import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Receipt,
  Plus,
  Pencil,
  Trash2,
  IndianRupee,
  FileText,
  Loader2,
  CheckCircle2
} from "lucide-react";
import client from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";

export default function ChargeMasterPage() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);

  const { data: charges = [], isLoading } = useQuery({
    queryKey: ["charge-masters"],
    queryFn: () => client.get("/charge-masters").then((r) => r.data?.data || []),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => client.delete(`/charge-masters/${id}`),
    onSuccess: () => {
      toast.success("Charge template deleted");
      queryClient.invalidateQueries({ queryKey: ["charge-masters"] });
    },
    onError: (err) => toast.error(err?.response?.data?.message || "Failed to delete"),
  });

  const handleOpenAdd = () => {
    setEditingCharge(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (charge) => {
    setEditingCharge(charge);
    setModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-950 flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-zinc-900 text-white">
              <Receipt className="w-5 h-5" />
            </div>
            Additional Charge Master
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Configure preset invoice fees (e.g. Loading, Pallet Wrapping, Sorting) available during dispatch invoicing.
          </p>
        </div>
        <Button
          onClick={handleOpenAdd}
          className="bg-zinc-950 hover:bg-zinc-800 text-white text-xs h-9 px-4 rounded-lg shadow-sm flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> Add Charge Preset
        </Button>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-xs text-zinc-400">Loading charge presets...</div>
      ) : charges.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-2xl p-12 text-center">
          <Receipt className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-zinc-900">No charge presets configured</h3>
          <p className="text-xs text-zinc-500 mt-1 mb-4">
            Add recurring fees that warehouse operators can check off during dispatch.
          </p>
          <Button onClick={handleOpenAdd} size="sm" variant="outline">
            Create First Preset
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {charges.map((c) => (
            <div
              key={c.id}
              className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm hover:border-zinc-300 transition flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-bold text-zinc-900">{c.name}</h3>
                  <span className="font-mono text-sm font-semibold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2.5 py-0.5 rounded-lg">
                    ₹ {Number(c.defaultAmount).toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-zinc-500 mt-2 line-clamp-2">
                  {c.description || "No description provided."}
                </p>
              </div>

              <div className="pt-4 mt-4 border-t border-zinc-100 flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenEdit(c)}
                  className="h-8 px-2.5 text-xs text-zinc-600 hover:text-zinc-900 border-zinc-200"
                >
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (window.confirm(`Delete preset "${c.name}"?`)) {
                      deleteMutation.mutate(c.id);
                    }
                  }}
                  className="h-8 px-2.5 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-zinc-200"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      <ChargeMasterFormDialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        initial={editingCharge}
      />
    </div>
  );
}

function ChargeMasterFormDialog({ open, onOpenChange, initial }) {
  const isEdit = Boolean(initial);
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: "", defaultAmount: "", description: "" });
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (initial) {
      setForm({
        name: initial.name || "",
        defaultAmount: initial.defaultAmount ?? "",
        description: initial.description || "",
      });
    } else {
      setForm({ name: "", defaultAmount: "", description: "" });
    }
  }, [initial, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Charge name is required");
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        defaultAmount: Number(form.defaultAmount) || 0,
        description: form.description?.trim() || null,
      };
      if (isEdit) {
        await client.patch(`/charge-masters/${initial.id}`, payload);
        toast.success("Charge preset updated");
      } else {
        await client.post("/charge-masters", payload);
        toast.success("Charge preset created");
      }
      queryClient.invalidateQueries({ queryKey: ["charge-masters"] });
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save charge preset");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white p-0 rounded-2xl shadow-2xl border border-zinc-200 overflow-hidden">
        <div className="px-6 pt-5 pb-4 border-b border-zinc-100 bg-zinc-50/60">
          <DialogTitle className="text-lg font-bold text-zinc-950">
            {isEdit ? "Edit Charge Preset" : "New Additional Charge Preset"}
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-0.5">
            Set the default rate and details for standard warehouse services.
          </p>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <Label className="text-xs font-semibold text-zinc-700 block mb-1.5">
              Charge Name *
            </Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Loading / Unloading, Pallet Wrapping"
              className="h-10 text-xs rounded-lg"
              required
            />
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-700 block mb-1.5">
              Default Amount (₹)
            </Label>
            <Input
              type="number"
              step="0.01"
              value={form.defaultAmount}
              onChange={(e) => setForm({ ...form, defaultAmount: e.target.value })}
              placeholder="0.00"
              className="h-10 text-xs font-mono rounded-lg"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-700 block mb-1.5">
              Description / Notes
            </Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Explanation of when this charge applies..."
              className="text-xs rounded-lg resize-none"
            />
          </div>

          <div className="pt-3 border-t border-zinc-100 flex items-center justify-end gap-2.5">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="h-9 px-4 text-xs rounded-lg"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !form.name.trim()}
              className="h-9 px-5 text-xs bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg"
            >
              {loading && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
              {isEdit ? "Save Changes" : "Create Preset"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
